import { useMemo } from "react"

import { DEFAULT_CURRENCY, useBounceGlobalValues, useLocale } from "@bounce/i18n"

import { useEnrichedGeoIp } from "./useEnrichedGeoIp"
import type { Country } from "../libs/bounce/bounceSdk.types.gen"
import type { BaseGenericInterpolations } from "../utils/interpolations/getBaseGenericInterpolations"
import { getBaseGenericInterpolations } from "../utils/interpolations/getBaseGenericInterpolations"

export type GenericInterpolations = BaseGenericInterpolations

type UseGenericInterpolationsOptions = {
  countries?: Country[]
  geoIpCountry?: string
}

/**
 * Returns an object with generic interpolated values based on the user's location and bounce countries.
 */
export const useGenericInterpolations = (props: UseGenericInterpolationsOptions): GenericInterpolations => {
  const countries = props.countries || []
  const locale = useLocale()
  const enrichedGeoIp = useEnrichedGeoIp({
    countries,
    geoIpCountry: props.geoIpCountry,
  })
  const currency = enrichedGeoIp.countryDetails?.currency ?? DEFAULT_CURRENCY
  const bounceGlobals = useBounceGlobalValues({ currency })

  const interpolations = useMemo(
    () =>
      getBaseGenericInterpolations({
        enrichedGeoIp,
        bounceGlobals,
        language: locale.language,
        currency,
      }),
    [enrichedGeoIp, bounceGlobals, locale.language, currency],
  )

  return interpolations
}
